import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { setQueryParams } from '@/hooks/route';
import route from '@/lib/route';
import { useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import EducatorSubscription from '../../EducatorSubscription';
import ExplorerDayPass from '../../ExplorerDayPass';
import ExplorerSubscription from '../../ExplorerSubscription';
import SingleHabitatDayPass from '../../SingleHabitatDayPass';
import SponsorshipSubscription from '../../SponsorshipSubscription';
import ProductsPromptModal from '../ProductsPromptModal';
import { setProductsPromptModalState } from './actions';
import { ProductType } from './types';

const renderCard = (
  productType: ProductType,
  habitatId: string | null | undefined,
  entryPoint: string | undefined,
  variant: 'primary' | 'secondary',
  forEducator?: boolean,
) => {
  switch (productType) {
    case 'single-habitat-day-pass':
      return habitatId ? <SingleHabitatDayPass habitatId={habitatId} entryPoint={entryPoint} /> : null;
    case 'all-habitats-day-pass':
      return <ExplorerDayPass entryPoint={entryPoint} variant={variant} forEducator={forEducator} />;
    case 'sponsorship':
      return habitatId ? <SponsorshipSubscription habitatId={habitatId} entryPoint={entryPoint} /> : null;
    case 'explorer-pass':
      return <ExplorerSubscription entryPoint={entryPoint} />;
    case 'educator-pass':
      return <EducatorSubscription entryPoint={entryPoint} />;
    default:
      return null;
  }
};

const ConnectedProductsPrompt = () => {
  const dispatch = useDispatch();
  const {
    visible,
    habitatId,
    primaryProductType,
    secondaryProductType,
    forEducator,
    redirectOnDismiss,
    resetQueryParamsOnDismiss,
    title,
    dismissButtonLabel,
    entryPoint,
  } = useSelector((state) => state.modals.productsPromptModal);
  const onDismiss = () => {
    dispatch(setProductsPromptModalState(null));
    if (resetQueryParamsOnDismiss) {
      setQueryParams({ ppt: undefined }, true);
    }
  };

  useEffect(() => {
    if (visible) {
      trackMP('page-view', {
        currentPage: 'ProductsPromptModal',
        pageType: 'Modal',
        habitatId,
        primaryProductType,
        secondaryProductType,
        entryPoint,
      });
    }
  }, [visible, habitatId, primaryProductType, secondaryProductType, entryPoint]);

  if (!visible) return null;

  return (
    <ProductsPromptModal
      onClickOutside={
        redirectOnDismiss
          ? () => {
              onDismiss();
              route('/home');
            }
          : onDismiss
      }
    >
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-6 desktop:px-6">
        <h2 className="preflight preflight-h1 px-4 text-center text-2xl/7 font-bold text-[#162B1B] empty:hidden">
          {title || null}
        </h2>
        {primaryProductType && renderCard(primaryProductType, habitatId, entryPoint, 'primary', forEducator)}
        {secondaryProductType && renderCard(secondaryProductType, habitatId, entryPoint, 'secondary', forEducator)}
        <div className="text-center">
          {redirectOnDismiss ? (
            <a
              href="/home"
              className="preflight preflight-a inline-block border-b border-current text-base font-medium leading-none text-grey-3 hover:text-grey-1"
              onClick={onDismiss}
            >
              {dismissButtonLabel}
            </a>
          ) : (
            <button
              type="button"
              className="preflight preflight-button inline-block border-b border-current text-base font-medium leading-none text-grey-3 hover:text-grey-1"
              onClick={onDismiss}
            >
              {dismissButtonLabel}
            </button>
          )}
        </div>
      </div>
    </ProductsPromptModal>
  );
};

export default ConnectedProductsPrompt;
